import { ADDRESS_PATTERN, NAME_PATTERN } from 'helpers/Constants';
import {
  primaryAndSecondaryAddressValidator,
  required,
  stateZipMatch
} from 'helpers/CommonValidations';
import moment from 'moment';

const SSN_PATTERN = new RegExp(
  '^(?!219099999|078051120|457555462)(?!666|000|9\\d{2})\\d{3}(?!00)\\d{2}(?!0{4})\\d{4}$'
);

const INVALID_CITY_PATTERN = /[0-9,]/;

const VALID_ZIP_PATTERN = /^\d+$/;

const validations = {
  firstName: (state) => {
    if (state.firstName && state.firstName.match(NAME_PATTERN)) {
      return {
        firstName:
          'Cannot include the following special characters: #%&{}\\<>*?/$!":@+`|=~^()'
      };
    }
    return required('firstName', state.firstName);
  },
  lastName: (state) => {
    if (state.lastName && state.lastName.match(NAME_PATTERN)) {
      return {
        lastName:
          'Cannot include the following special characters: #%&{}\\<>*?/$!":@+`|=~^()'
      };
    }
    return required('lastName', state.lastName);
  },
  primaryPhoneNumber: (state) => {
    if (
      state.primaryPhoneNumber.length > 0 &&
      state.primaryPhoneNumber.length < 10
    ) {
      return { primaryPhoneNumber: 'Must be a 10 digit number' };
    }
    return required('primaryPhoneNumber', state.primaryPhoneNumber);
  },
  secondaryPhoneNumber: (state) => {
    if (
      state.secondaryPhoneNumber.length > 0 &&
      state.secondaryPhoneNumber.length < 10
    ) {
      return { secondaryPhoneNumber: 'Must be a 10 digit number' };
    }
    return { secondaryPhoneNumber: '' };
  },
  homeStreetAddress: (state) => {
    const address = state.homeStreetAddress;

    if (address && address.match(ADDRESS_PATTERN)) {
      return {
        homeStreetAddress:
          'Please enter a valid address. P.O. Boxes are not accepted'
      };
    }

    if (address && state.secondaryAddress) {
      return primaryAndSecondaryAddressValidator(state);
    }

    return required('homeStreetAddress', address);
  },
  secondaryAddress: (state) => {
    const secondary = state.secondaryAddress;

    if (secondary && secondary.match(ADDRESS_PATTERN)) {
      return {
        secondaryAddress:
          'Please enter a valid address. P.O. Boxes are not accepted'
      };
    }

    if (state.homeStreetAddress && secondary) {
      return primaryAndSecondaryAddressValidator(state);
    }

    return { secondaryAddress: '' };
  },
  city: (state) => {
    if (INVALID_CITY_PATTERN.test(state.city)) {
      return { city: 'Please enter a valid city' };
    }

    return required('city', state.city);
  },
  state: (state) => {
    if (state.state && state.zip) {
      return stateZipMatch('state', state.state, 'zip', state.zip);
    }
    return required('state', state.state);
  },
  zip: (state) => {
    const zip = state.zip;

    if (zip) {
      if (zip.length !== 5) {
        return { zip: 'Must be 5 digits' };
      }

      if (!VALID_ZIP_PATTERN.test(zip)) {
        return { zip: 'Please enter a valid zip code' };
      }

      if (state.state) {
        return stateZipMatch('state', state.state, 'zip', zip);
      }
    }

    return required('zip', zip);
  },
  dateOfBirth: (state) => {
    if (!state.dateOfBirth) {
      return required('dateOfBirth', state.dateOfBirth);
    }
    const date = moment(state.dateOfBirth, 'MM/DD/YYYY');
    const isDateValid = date.isValid();
    if (!isDateValid || state.dateOfBirth.length < 8) {
      return { dateOfBirth: 'Must be a valid date.' };
    }
    const age = moment().diff(moment(state.dateOfBirth, 'MM/DD/YYYY'), 'years');
    const over18 = age >= 18;
    const under121 = age < 121;
    if (!over18) return { dateOfBirth: 'Must be 18 or older.' };
    if (!under121) return { dateOfBirth: 'Max age is 120' };
    return { dateOfBirth: '' };
  },
  socialSecurityNumber: (state) => {
    const value = state.socialSecurityNumber;
    if (!value) {
      return required('socialSecurityNumber', value);
    }
    if (!value.match(SSN_PATTERN)) {
      return { socialSecurityNumber: 'Invalid Social Security Number' };
    }
    if (state.type === 'lde') {
      return { socialSecurityNumber: '' };
    }
    const confirmValidation = validations.confirmSocialSecurityNumber(state);
    return { socialSecurityNumber: '', ...confirmValidation };
  },
  confirmSocialSecurityNumber: (state) => {
    const id = 'confirmSocialSecurityNumber';
    const value = state[id];
    if (!value) {
      return required(id, value);
    }
    if (value !== state.socialSecurityNumber) {
      return { [id]: 'Must match Social Security Number.' };
    }
    return { [id]: '' };
  },
  isReferral: (state) => {
    return required('isReferral', state.isReferral);
  },
  addressConfirmation: (state) => {
    if (state.type === 'refinance' && !state.isAddressConfirmed) {
      return {
        addressConfirmation:
          'You must confirm your address is correct by checking this box.'
      };
    }
    return {
      addressConfirmation: ''
    };
  }
};

export default validations;
